import React, { useState, useEffect, useRef } from 'react'
import { useFormik } from "formik";
import * as Yup from "yup";
import { FaTimes, FaSpinner } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  uploadCreative,
  getZonalUsers,
  getPendingCreatives,
  getCommanDealers,
  getCreatives,
  reAssignCreative,
} from "../../redux/slices/apiSlice";
import { getAllRequestsByAccountName } from "./CreativeConstants";
import dragIcon from "../../assets/images/drag_icon.svg";
import UseToast from "../../hooks/useToast";
import { agencyUserRoles } from "../../utils";
import Loader from "../../utils/Loader";
import fileIcon from '../../assets/images/file.png'
import { RiInformation2Fill } from "react-icons/ri";
import { FILE_TYPES, FILE_SIZE } from '../../utils'
import { getFileIcon } from "../../utils";



const UploadCreative = ({
  close,
  user,
  accountName,
  userRole,
  reassignedCreativeItem,
  setReassignedCreativeItem,
  openCreativeUpload
}) => {
  const dispatch = useDispatch();
  const { status, error, zonalUsers } = useSelector((state) => state.api);
  const [selecting, setSelecting] = useState(false);
  const [isNewRequest, setIsNewRequest] = useState(true); // Default is required
  const { successToast, errorToast } = UseToast();
  const [channelsData, setChannelsData] = useState([]);
  const [zonesData, setZonesData] = useState([]);
  const [regionsData, setRegionsData] = useState([]);
  const [dealersData, setDealersData] = useState([]);
  const [allDealerData, setAllDealerData] = useState([]);
  const [assignOpitons, setAssignOpitons] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);


  // const validationSchema =   Yup.object({
  //   file: Yup.mixed().required("A file is required"),
  //   requestName: Yup.string().required("Request Name is required"),
  //   assignedTo: Yup.array().min(1, "At least one user must be assigned"),
  //   reason: Yup.string().required("Reason is required"),
  // });
  const validationSchema = React.useMemo(
    () =>
      Yup.object({
        // file: Yup.mixed()
        //   .nullable()
        //   .test("file-required", "A file is required", function (value) {
        //     if (isNewRequest) {
        //       return !!value;
        //     }
        //     return true;
        //   }),
        file: Yup.mixed()
          .nullable() 
          .test("file-required", "A file is required", function (value) {
            if (isNewRequest) {
              return !!value;
            }
            return true;
          })
          .test(
            "fileType",
            "Invalid file type. Please upload a supported file format.",
            function (file) { 
              if (!file) return true;
              const validMimeTypes = [
                ...FILE_TYPES.DOC_TYPES,
                ...FILE_TYPES.IMAGE_TYPES,
                ...FILE_TYPES.VIDEO_TYPES,
              ];
              const validExtensions = [".psd"]; 
              const fileExtension = file.name.split('.').pop().toLowerCase();
              if (validExtensions.includes(`.${fileExtension}`) || validMimeTypes.includes(file.type)) {
                return true;
              }
              return false;

              // return (
              //   [...FILE_TYPES.DOC_TYPES, ...FILE_TYPES.IMAGE_TYPES, ...FILE_TYPES.VIDEO_TYPES]
              //     .includes(file.type)
              // );
            }
          )
          .test("fileSize", function (file) {
            if (!file) return true; 
            const fileTypeCategory = file.type.startsWith("image/")
              ? "IMAGE_MAX_SIZE"
              : file.type.startsWith("video/")
                ? "VIDEO_MAX_SIZE"
                : "DOC_MAX_SIZE";
            const maxSize = FILE_SIZE[fileTypeCategory];
            const maxSizeMB = maxSize / (1024 * 1024);
            return file.size <= maxSize
              ? true
              : this.createError({
                message: `File "${file.name}" exceeds the allowed limit of ${maxSizeMB} MB.`,
              });
          }),
        reason: Yup.string().test(
          "reason-required",
          "Reason is required",
          function (value) {
            if (!isNewRequest) {
              return !!value;
            }
            return true;
          }
        ),
        requestName: Yup.string().required("Request Name is required"),
        requestedFileName: Yup.string().required("File Name is required"),

        // channel: Yup.object({
        //   label: Yup.string().required("Please select a value for channel"),
        //   value: Yup.string().required("Please select a value for channel"),
        // }).required("Please select a value for channel"),
        // assignedTo: Yup.object().shape({
        //   label: Yup.string().required("Please select a value for Assigned To"),
        //   value: Yup.string().required("Please select a value for Assigned To"),
        // }),
        // assign: Yup.object().shape({
        //   label: Yup.string().required("Please select a value for Assign"),
        //   value: Yup.string().required("Please select a value for Assign"),
        // }),
        channel: Yup.mixed()
          .nullable()
          .typeError("Please select a value for channel")
          .test("channel-required", "Please select a value for channel", function (value) {
            if (user?.role === "franchise") {
              if (Array.isArray(value)) return false;
              return value && typeof value === "object" && value.label && value.value;
            }
            return true;
          }),
        assignedTo: Yup.mixed()
          .nullable()
          .typeError("Please select a value for Assigned To")
          .test("assignedTo-required", "Please select a value for Assigned To", function (value) {
            if (["agency_user", "agency_admin", "account_managers"].includes(user?.role)) {
              if (Array.isArray(value)) return false;
              return value && typeof value === "object" && value.label && value.value;
            }
            return true;
          }),
        assign: Yup.mixed()
          .nullable()
          .typeError("Please select a value for Assign")
          .test("assign-required", "Please select a value for Assign", function (value) {
            if (["agency_user", "agency_admin", "account_managers"].includes(user?.role)) {
              if (Array.isArray(value)) return false;
              return value && typeof value === "object" && value.label && value.value;
            }
            return true;
          })
      }),
    [isNewRequest, user?.role]
  );

  useEffect(() => {
    dispatch(getZonalUsers());
    dispatch(getCommanDealers());
  }, [dispatch]);

  useEffect(() => {
    formik.resetForm({
      values: {
        file: null,
        requestName: "",
        requestedFileName: "",
        description: "",
        reason: "",
        assignedTo: null,
        assign: null,
        channel: null,
      }
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [openCreativeUpload]);

  useEffect(() => {
    try {
      const decodedData = JSON.parse(sessionStorage.getItem("decodedData"));
      const channels = JSON.parse(decodedData["custom:channel"] || "[]");
      channels?.length && setChannelsData(
        channels.map(role => ({ label: role, value: role }))
      );


    } catch (e) {
      console.log("error is -", e);
    }

    return () => {
      setReassignedCreativeItem(null);
    };
  }, []);

  React.useEffect(() => {
    formik.validateForm();
  }, [isNewRequest]);

  // const userOptions = zonalUsers?.data?.map((user) => ({
  //   value: user._id,
  //   label: user.user_name,
  // }));

  const formik = useFormik({
    initialValues: {
      file: null,
      requestName: "",
      requestedFileName: "",
      description: "",
      reason: "",
      assignedTo: null,
      assign: null,
      channel: null,
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      setLoading(true);
      if (reassignedCreativeItem) {

        let payload = {
          creative_id: reassignedCreativeItem?._id,
          // file: null,
          // requestName: values?.requestName,
          // description: values?.description,
          reason: values?.reason,
          // assignedTo: values.assignedTo?.value,
          // channel: values.channel?.value,
          // assign: values.assign?.value,
          // assignedBy: user._id,
          // account_name: accountName,
          // s3_url: reassignedCreativeItem?.s3_file_name,
        };

        dispatch(reAssignCreative(payload)).then(() => {
          setLoading(false);

          if (getAllRequestsByAccountName.includes(userRole)) {
            dispatch(
              getCreatives({
                payload: { userId: user?._id, account_name: accountName },
              })
            );
          } else {
            dispatch(
              getCreatives({
                payload: { userId: user?._id },
              })
            );
          }
          resetForm({
            values: {
              file: null,
              requestName: "",
              requestedFileName: "",
              description: "",
              assignedTo: null,
              assign: null,
              reason: "",
              channel: null
            },
          });
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          close();
          setReassignedCreativeItem(null);
        });
      } else {


        try {
          const decodedData = JSON.parse(sessionStorage.getItem("decodedData"));
          const clients = JSON.parse(decodedData["custom:clients"] || "[]");
          if (clients?.length) {
            if (user.role == "franchise") {
              const userRoleCode = JSON.parse(decodedData["custom:userRoleCode"])

              dispatch(getCommanDealers({ payload: { client: clients[0]["account_name"]?.toLowerCase(), parent_dealer_code: userRoleCode }, headers: { idToken: sessionStorage.getItem("token"), accessToken: sessionStorage.getItem("accessToken") } })).then((response) => {

                if (response?.error?.message || !response?.payload) {
                  errorToast(response?.error?.message || "OOPS! Something went wrong.");
                  return;
                }
                if (!response?.payload?.data?.[0]) {
                  setLoading(false);
                  errorToast("Franchise not found in glocal, Please try again!");
                  return;
                }

                const formData = new FormData();
                formData.append("uploadedFile", values.file);
                formData.append("requestName", values.requestName);
                formData.append("requestedFileName", values.requestedFileName);

                formData.append("description", values.description);

                formData.append("assignedBy", user._id);
                formData.append("account_name", accountName);
                const assignedToData = {
                  "AssignedUser": response?.payload?.data?.[0]?._id,
                  "userRoleCode": decodedData["custom:userRole"],
                  "region": response?.payload?.data?.[0]?.region,
                  "parentDealerCode": response?.payload?.data?.[0]?.parent_dealer_code,
                  "parentDealerName": response?.payload?.data?.[0]?.parent_dealer_name || "",
                  "zone": response?.payload?.data?.[0]?.zone
                };
                formData.append("assignedTo", JSON.stringify(assignedToData));
                formData.append("channel", values.channel.value);

                // if (formik.values.assignedTo?.value === "Zonal") {
                //   assignedToData[0].zone = formik.values.assign?.value;
                // } else if (formik.values.assignedTo?.value === "Regional") {
                //   assignedToData[0].region = formik.values.assign?.value;
                // } else if (formik.values.assignedTo?.value === "Franchise") {
                //   assignedToData[0].parentDealerCode = formik.values.assign?.value;
                // }

                // formData.append("assign", values.assign?.value);

                dispatch(uploadCreative({ formData })).then(() => {
                  setLoading(false);

                  if (getAllRequestsByAccountName.includes(userRole)) {
                    dispatch(
                      getCreatives({
                        payload: { userId: user?._id, account_name: accountName },
                      })
                    );
                  } else {
                    dispatch(
                      getCreatives({
                        payload: { userId: user?._id },
                      })
                    );
                  }
                  resetForm({
                    values: {
                      file: null,
                      requestName: "",
                      requestedFileName: "",
                      description: "",
                      assignedTo: null,
                      channel: null,
                      assign: null,
                      reason: "",
                    },
                  });
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                  removeFile();
                  close();
                  setReassignedCreativeItem(null);
                });


              }).catch((error) => {
                console.log("error --------- ", error)
                setLoading(false);

                errorToast("Oops! Something went wrong in api response.");
              });
            } else {

              let dealerData = null;
              let channel, zone, region, dealer;
              if (zonesData?.length) {
                [channel, zone] = values.assign.label.split(" - ");
              } else if (regionsData?.length) {
                [channel, zone, region] = values.assign.label.split(" - ");
              } else if (dealersData?.length) {
                dealerData = allDealerData.find(obj => obj.parent_dealer_code === values.assign.value);
                [channel, zone, region, dealer] = values.assign.label.split(" - ");
              }

              const formData = new FormData();
              formData.append("uploadedFile", values.file);
              formData.append("requestName", values.requestName);
              formData.append("requestedFileName", values.requestedFileName);

              formData.append("description", values.description);

              formData.append("assignedBy", user._id);
              formData.append("account_name", accountName);
              const assignedToData = {
                "AssignedUser": dealerData?._id || "",
                "userRoleCode": zonesData?.length ? "zonal" : regionsData?.length ? "region" : "franchise",
                "region": dealerData?.region || region,
                "parentDealerCode": dealerData?.parent_dealer_code || "",
                "parentDealerName": dealerData?.parent_dealer_name || dealer || "",
                "zone": dealerData?.zone || zone
              };
              formData.append("assignedTo", JSON.stringify(assignedToData));
              formData.append("channel", values?.channel?.value || channel);

              // if (formik.values.assignedTo?.value === "Zonal") {
              //   assignedToData[0].zone = formik.values.assign?.value;
              // } else if (formik.values.assignedTo?.value === "Regional") {
              //   assignedToData[0].region = formik.values.assign?.value;
              // } else if (formik.values.assignedTo?.value === "Franchise") {
              //   assignedToData[0].parentDealerCode = formik.values.assign?.value;
              // }

              // formData.append("assign", values.assign?.value);

              dispatch(uploadCreative({ formData })).then(() => {
                setLoading(false);

                if (getAllRequestsByAccountName.includes(userRole)) {
                  dispatch(
                    getCreatives({
                      payload: { userId: user?._id, account_name: accountName },
                    })
                  );
                } else {
                  dispatch(
                    getCreatives({
                      payload: { userId: user?._id },
                    })
                  );
                }
                resetForm({
                  values: {
                    file: null,
                    requestName: "",
                    requestedFileName: "",
                    description: "",
                    assignedTo: null,
                    channel: null,
                    assign: null,
                    reason: "",
                  },
                });
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                removeFile();
                close();
                setReassignedCreativeItem(null);
              });





            }
          }
        } catch (err) {
          errorToast("Oops! Something went wrong in api response.");
          setLoading(false);

          console.error("Failed to parse token:", err);
          return null;
        }



      }
    },
  });

  const assignedToValue = formik.values.assignedTo?.value;

  let userOptions = [];

  if (assignedToValue === "Regional") {
    userOptions = ["C1", "C2", "C3"].map((region) => ({
      value: region,
      label: region,
    }));
  } else if (assignedToValue === "Zonal") {
    userOptions = ["East", "West", "North", "South"].map((zone) => ({
      value: zone,
      label: zone,
    }));
  } else if (assignedToValue === "Franchise") {
    userOptions = [2345, 11145, 12346].map((franchise) => ({
      value: franchise,
      label: franchise,
    }));
  } else {
    userOptions =
      formik.values.assignedTo?.data?.map((user) => ({
        value: user._id,
        label: user.user_name,
      })) || [];
  }

  const handleFileChange = (e) => {
    setSelecting(true);
    // setTimeout(() => {
      setSelecting(false);
      const selectedFile = e.target.files[0];
      if (selectedFile) {
        const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
        if (fileExtension === 'psd' && !selectedFile.type) {
          const newFile = new File([selectedFile], selectedFile.name, { type: 'application/x-photoshop' });
          formik.setFieldValue("file", newFile);
        } else {
          console.log('File with type:', selectedFile);
          formik.setFieldValue("file", selectedFile);
        }

      }
    // }, 1500);
  };

  const handleAssignToChange = (assignTo) => {



    try {
      setLoading(true);
      const decodedData = JSON.parse(sessionStorage.getItem("decodedData"));
      const clients = JSON.parse(decodedData["custom:clients"] || "[]");
      if (clients?.length) {
        dispatch(getCommanDealers({ payload: { client: clients[0]["account_name"]?.toLowerCase() }, headers: { idToken: sessionStorage.getItem("token"), accessToken: sessionStorage.getItem("accessToken") } })).then((response) => {

          if (response?.error?.message || !response?.payload) {
            errorToast(response?.error?.message || "OOPS! Something went wrong.");
            return;
          }
          setAllDealerData([]);
          if (response?.payload?.data) {
            setAllDealerData(response?.payload?.data);
            const channelsSet = new Set();
            const zonesSet = new Set();
            const regionsSet = new Set();
            const dealersSet = new Set();

            response?.payload?.data?.forEach(item => {
              item.channel.forEach(channel => {
                channelsSet.add(channel);

                const zoneKey = `${channel}||${item.zone}`;
                zonesSet.add(zoneKey);

                const regionKey = `${channel}||${item.zone}||${item.region}`;
                regionsSet.add(regionKey);

                const dealerKey = `${channel}||${item.zone}||${item.region}||${item.parent_dealer_name}||${item.parent_dealer_code}`;
                dealersSet.add(dealerKey);
              });
            });

            const channels = Array.from(channelsSet).map(channel => ({
              label: channel,
              value: channel
            }));

            const zones = Array.from(zonesSet).map(entry => {
              const [channel, zone] = entry.split("||");
              return {
                label: `${channel.toUpperCase()} - ${zone}`,
                value: zone
              };
            });

            const regions = Array.from(regionsSet).map(entry => {
              const [channel, zone, region] = entry.split("||");
              return {
                label: `${channel.toUpperCase()} - ${zone} - ${region}`,
                value: region
              };
            });

            const dealers = Array.from(dealersSet).map(entry => {
              const [channel, zone, region, dealerName, dealerCode] = entry.split("||");
              return {
                label: `${channel.toUpperCase()} - ${zone} - ${region} - ${dealerName}`,
                value: dealerCode
              };
            });

            setChannelsData([]);
            setZonesData([]);
            setRegionsData([]);
            setDealersData([]);
            setAssignOpitons([]);
            if (assignTo.value == "Zonal") {
              setZonesData(zones);
              setAssignOpitons(zones);
            } else if (assignTo.value == "Regional") {
              setRegionsData(regions);
              setAssignOpitons(regions);
            } else if (assignTo.value == "Franchise") {
              setDealersData(dealers);
              setAssignOpitons(dealers);
            }
            setLoading(false);
          } else {
            errorToast("No Data Found.");
            setLoading(false);

          }



        }).catch((error) => {
          console.log("error --------- ", error)
          setLoading(false);

          errorToast("Oops! Something went wrong in api response.");
        });
      }
    } catch (err) {
      errorToast("Oops! Something went wrong in api response.");
      setLoading(false);

      console.error("Failed to parse token:", err);
      return null;
    }



    // const decodedData = JSON.parse(sessionStorage.getItem("decodedData")); 
    // const clients = JSON.parse(decodedData["custom:clients"] || "[]");
    // console.log("clients 000000 ", clients);
    // const userRoleCode = JSON.parse(decodedData["custom:userRoleCode"])
    // const channel = JSON.parse(decodedData["custom:channel"])
    // if(userRoleCode?.length && channel?.length){
    //   if(user.role == "franchise"){
    //     dispatch(getCommanDealers({payload: {client: clients[0]["account_name"]?.toLowerCase(), dealer_code: userRoleCode.join(",")}, headers: {idToken:sessionStorage.getItem("token") , accessToken: sessionStorage.getItem("accessToken") }}));
    //   }else {

    //   }
    // }



  }


  const removeFile = () => {
    formik.setFieldValue("file", null);
  };

  useEffect(() => {
    if (reassignedCreativeItem) {
      if (agencyUserRoles.includes(user?.role) && reassignedCreativeItem?.assignedTo?.length) {
        const assignedTo = reassignedCreativeItem?.assignedTo[0];

        if (assignedTo?.userRoleCode == "zonal") {
          formik.setFieldValue("assignedTo", { label: "Zonal", value: "Zonal" });
          const zoneKey = `${reassignedCreativeItem?.channel} - ${assignedTo?.zone}`;
          formik.setFieldValue("assign", { label: zoneKey, value: zoneKey });
        } else if (assignedTo?.userRoleCode == "region") {
          formik.setFieldValue("assignedTo", { label: "Regional", value: "Regional" });
          const regionKey = `${reassignedCreativeItem?.channel} - ${assignedTo?.zone} - ${assignedTo?.region}`;
          formik.setFieldValue("assign", { label: regionKey, value: regionKey });
        } else if (assignedTo?.userRoleCode == "franchise") {
          formik.setFieldValue("assignedTo", { label: "Franchise", value: "Franchise" });
          const franchiseKey = `${reassignedCreativeItem?.channel} - ${assignedTo?.zone} - ${assignedTo?.region} - ${assignedTo?.parentDealerName}`;
          formik.setFieldValue("assign", { label: franchiseKey, value: franchiseKey });
        }
      }


      //   [
      //     {

      //         "userRoleCode": "zonal",
      //         "zone": "West",
      //         "region": null,
      //         "parentDealer": null,
      //         "_id": "6800ae2be74bf8ce94abbdbb"
      //     }
      // ]


      if (reassignedCreativeItem?.channel) {
        setChannelsData([{ label: reassignedCreativeItem.channel, value: reassignedCreativeItem.channel }]);
        formik.setFieldValue("channel", { label: reassignedCreativeItem.channel, value: reassignedCreativeItem.channel });
      }
      formik.setFieldValue(
        "requestName",
        reassignedCreativeItem?.requestName || ""
      );
      formik.setFieldValue(
        "requestedFileName",
        reassignedCreativeItem?.requestedFileName || ""
      );

      formik.setFieldValue("reason", reassignedCreativeItem?.reason || "");
      formik.setFieldValue(
        "description",
        reassignedCreativeItem?.description || ""
      );

      // formik.setFieldValue(
      //   "assignedTo",
      //   reassignedCreativeItem?.assignedTo?._id
      //     ? [
      //       {
      //         label: reassignedCreativeItem?.assignedTo?.user_name,
      //         value: reassignedCreativeItem?.assignedTo?._id,
      //       },
      //     ]
      //     : []
      // );
      setIsNewRequest(false);
    } else {
      formik.setFieldValue("requestName", "");
      formik.setFieldValue("requestedFileName", "");

      formik.setFieldValue("description", "");

      formik.setFieldValue("reason", "");
      formik.setFieldValue("assignedTo", []);
      formik.setFieldValue("channel", []);
      formik.setFieldValue("assigned", []);
      formik.setFieldValue("file", null);
      setIsNewRequest(true);
    }
  }, [reassignedCreativeItem]);

  return (
    <>
      {loading ? <Loader></Loader> : <></>}
      <form onSubmit={formik.handleSubmit} className="flex flex-col w-full creativeFormSection">
        <div className="upload_creative_row">
          <label>Request Name<span class="text-red-500 text-xs align-super">*</span>
          </label>
          <input
            type="text"
            placeholder="Request Name"
            className="border p-2 rounded"
            {...formik.getFieldProps("requestName")}
            disabled={reassignedCreativeItem}
          />
          {formik.touched.requestName && formik.errors.requestName && (
            <p className="text-red-500 text-[12px]">
              {formik.errors.requestName}
            </p>
          )}
        </div>
        {agencyUserRoles.includes(user?.role) &&

          <>

            <div className="upload_creative_row">
              <label>Request For<span class="text-red-500 text-xs align-super">*</span></label>
              <Select
                options={[
                  { label: "Zonal", value: "Zonal" },
                  { label: "Regional", value: "Regional" },
                  { label: "Franchise", value: "Franchise" },
                ]}
                className='customSelectBox'
                placeholder="Request For"
                value={formik.values.assignedTo}
                onChange={(value) => { handleAssignToChange(value); formik.setFieldValue("assignedTo", value); }}
                isDisabled={reassignedCreativeItem}
              />
              {formik.touched.assignedTo && formik.errors.assignedTo && (
                <p className="text-red-500 text-[12px]">{formik.errors.assignedTo}</p>
              )}
            </div>

            <div className="upload_creative_row">
              <label>Assign To<span class="text-red-500 text-xs align-super">*</span></label>
              <Select
                className='customSelectBox'
                options={assignOpitons}
                placeholder="Assign To"
                value={formik.values.assign}
                onChange={(value) => formik.setFieldValue("assign", value)}
                isDisabled={reassignedCreativeItem}
              />
              {formik.touched.assign && formik.errors.assign && (
                <p className="text-red-500 text-[12px]">{formik.errors.assign}</p>
              )}
            </div>

          </>
        }

        {!agencyUserRoles.includes(user?.role) &&
          <div className="upload_creative_row">
            <label>Channel<span class="text-red-500 text-xs align-super">*</span></label>
            <Select
              options={channelsData}
              className='customSelectBox'
              placeholder="Channel"
              value={formik.values.channel}
              onChange={(value) => { formik.setFieldValue("channel", value); }}
              isDisabled={reassignedCreativeItem}
            />
            {formik.touched.channel && formik.errors.channel && (
              <p className="text-red-500 text-[12px]">{formik.errors.channel}</p>
            )}
          </div>
        }




        {!isNewRequest && (
          <input
            type="text"
            placeholder="Reason"
            className="border p-2 rounded"
            {...formik.getFieldProps("reason")}
          />
        )}

        {formik.touched.reason && formik.errors.reason && (
          <p className="text-red-500 text-[12px]">{formik.errors.reason}</p>
        )}

        {!reassignedCreativeItem && (
          <div className="upload_creative_row">
            <div className="relative w-full">
              <label className="flex justify-between items-center cursor-pointer w-full">
                <span>Upload<span class="text-red-500 text-xs align-super">*</span></span>
                <div
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  className="relative"
                >
                  <RiInformation2Fill className="text-gray-400" />
                  {showTooltip && (
                    <div className="absolute top-6 right-0 bg-white text-gray-500 rounded shadow p-2 z-10 break-words max-w-xs">
                      .jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.mp4,.avi,.mov,.wmv,.flv,.mkv,.webm,.pdf,.xls,.xlsx,.doc,.docx,.psd
                    </div>
                  )}
                </div>
              </label>
            </div>
            <label className="block border-dashed border-2 border-[#1890FF] rounded p-6 text-center cursor-pointer relative">
              <input
                type="file"
                className="hidden"
                onChange={handleFileChange}
                ref={fileInputRef}
                accept='image/*, video/*, application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, .psd'
              />
              {selecting ? (
                <div className="absolute inset-0 bg-gray-100 bg-opacity-20 flex justify-center items-center">
                  <FaSpinner className="animate-spin" />
                </div>
              ) : (
                <div className="drag_input">
                  <img src={dragIcon} alt="logo" />
                  <p>
                    Create or Drag File Upload
                    {/* <br />
                    <span className="text-[#7C7E81]">(64 width x 64 height)</span> */}
                  </p>
                </div>
              )}
            </label>
            {formik.touched.file && formik.errors.file && (
              <p className="text-red-500 text-[12px]">{formik.errors.file}</p>
            )}
          </div>
        )}
 {formik.values.file && (
                  <div className='selected-file'>
                    {formik.values.file.name} - (
                    {`${formik.values.file.size
                        ? (formik.values.file.size / (1024 * 1024)).toFixed(2)
                        : 0
                      } MB`}
                    )
                  </div>
                )}
        {formik.values.file && !reassignedCreativeItem && (
          <div className="mt-4 border p-2 rounded-lg relative w-32"> 
            <img
              src={formik.values.file.type.startsWith('image') ? URL.createObjectURL(formik.values.file) : (getFileIcon(formik.values.file?.type) || fileIcon)}
              alt={formik.values.file.name}
              className="w-full h-24 object-cover rounded"
            />
            <button
              type="button"
              onClick={removeFile}
              className="absolute top-1 right-1 text-blue-500 bg-white rounded-full p-1"
            >
              <FaTimes size={12} />
            </button>
          </div>
        )}

        {reassignedCreativeItem?.s3_file_name && (
          <div className="mt-4 border p-2 rounded-lg relative w-32">
            {reassignedCreativeItem.file_type.startsWith('image') ? 
            <img
              src={reassignedCreativeItem?.s3_file_name}
              alt={reassignedCreativeItem?.origin_name}
              className="w-full h-24 object-cover rounded"
            /> : (getFileIcon(reassignedCreativeItem.file_type) || fileIcon)
}
          </div>
        )}

        <div className="upload_creative_row">
          <label>File Name<span class="text-red-500 text-xs align-super">*</span></label>
          <input
            type="text"
            placeholder="Request Name"
            className="border p-2 rounded"
            {...formik.getFieldProps("requestedFileName")}
            disabled={reassignedCreativeItem}
          />
          {formik.touched.requestedFileName && formik.errors.requestedFileName && (
            <p className="text-red-500 text-[12px]">
              {formik.errors.requestedFileName}
            </p>
          )}
        </div>

        <div className="upload_creative_row">
          <label>Description :</label>
          {/* <textarea
          placeholder="Add description"
          class="border p-2 rounded"
        ></textarea> */}
          <textarea
            placeholder="Add description"
            className="border p-2 rounded"
            {...formik.getFieldProps("description")}
            disabled={reassignedCreativeItem}
          />

        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            className="p-2 w-20 text-white bg-[#1890FF] hover:bg-black rounded"
            disabled={status === "loading"}
          >
            {status === "loading" ? (
              <FaSpinner className="animate-spin" />
            ) : (
              "Upload"
            )}
          </button>
        </div>
        {status === "failed" && <p className="text-red-500 mt-2">{error}</p>}
        {status === "succeeded" && (
          <p className="text-green-500 mt-2">Upload successful!</p>
        )}
      </form>
    </>

  );
};

export default UploadCreative;
